<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useQuery } from "@tanstack/vue-query";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import {
  getWorkforceActivity,
  type ActivityBucket,
  type WorkerActivityRow,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import WorkerAvatar from "@/components/WorkerAvatar.vue";

const { data, isPending, isError, error } = useQuery({
  queryKey: ["workforce-activity"],
  queryFn: () => getWorkforceActivity(),
});

const BUCKETS: Record<Exclude<ActivityBucket, "no_pin">, { label: string; color: string }> = {
  on_site_today: { label: "On site today", color: "#2A9F15" },
  within_7d: { label: "Within 7 days", color: "#DB562B" },
  older: { label: "Older", color: "#A89EA0" },
  // App-use location (no GPS sign-on yet) — distinct blue so it reads as
  // "where the app was used", not an evidential on-site sign-on.
  app: { label: "App location", color: "#2563EB" },
};

// The most recent interaction with the app — the later of the Auth0 logon
// (any surface) and the last-seen ping (mobile). Drives the "recently active"
// sort so web logins surface too, not just mobile pings.
function lastActive(w: WorkerActivityRow): string | null {
  const a = w.last_seen_at;
  const b = w.last_login;
  if (a && b) return a > b ? a : b;
  return a ?? b;
}

// Workers with no map pin — recently-active first, so app logins surface at the
// top instead of being buried alphabetically.
const noPin = () =>
  (data.value?.workers ?? [])
    .filter((w) => w.bucket === "no_pin")
    .sort((a, b) => (lastActive(b) ?? "").localeCompare(lastActive(a) ?? ""));

// Adoption signals: who has opened the app (a last-seen ping) / logged in.
const usedAppCount = () =>
  (data.value?.workers ?? []).filter((w) => w.last_seen_at != null).length;
const loggedInCount = () =>
  (data.value?.workers ?? []).filter((w) => w.last_login != null).length;
const lastUsedAt = () => {
  const ts = (data.value?.workers ?? [])
    .map((w) => lastActive(w))
    .filter((t): t is string => t != null);
  return ts.length ? ts.reduce((a, b) => (a > b ? a : b)) : null;
};

function ago(iso: string | null): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] ?? c,
  );
}

// ---- Leaflet ----
const mapEl = ref<HTMLElement | null>(null);
let map: L.Map | null = null;
let markers: L.LayerGroup | null = null;

function pinIcon(bucket: Exclude<ActivityBucket, "no_pin">): L.DivIcon {
  const cls = bucket === "on_site_today" ? "wf-pin wf-pin--pulse" : "wf-pin";
  return L.divIcon({
    className: "",
    html: `<span class="${cls}" style="background:${BUCKETS[bucket].color}"></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

function draw(): void {
  if (!map) return;
  markers?.remove();
  markers = L.layerGroup().addTo(map);
  const pts: L.LatLngExpression[] = [];
  for (const w of data.value?.workers ?? []) {
    if (w.latitude == null || w.longitude == null || w.bucket === "no_pin") continue;
    const b = w.bucket;
    L.marker([w.latitude, w.longitude], { icon: pinIcon(b) })
      .bindPopup(
        `<strong>${escapeHtml(w.name)}</strong><br>${BUCKETS[b].label}` +
        `<br>Last sign-on: ${w.last_signon_on ?? "—"}` +
        `<br>Last logon: ${ago(w.last_login)}` +
        `<br>Last app activity: ${ago(w.last_seen_at)}`,
      )
      .addTo(markers);
    pts.push([w.latitude, w.longitude]);
  }
  if (pts.length) map.fitBounds(L.latLngBounds(pts).pad(0.25));
}

// Init is idempotent + only runs once the container is actually in the DOM.
// The map div lives inside `v-else-if="data"`, so on a slow first load it
// doesn't exist at onMounted — we (re)try whenever data arrives.
function initMap(): void {
  if (map || !mapEl.value) return;
  map = L.map(mapEl.value).setView([54.5, -3.5], 5); // UK
  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution: "&copy; OpenStreetMap",
    maxZoom: 19,
  }).addTo(map);
  // The container often sizes after layout — recalc so tiles fill it.
  setTimeout(() => map?.invalidateSize(), 0);
}

onMounted(() => {
  initMap(); // covers the cached/instant-data case
  draw();
});
watch(data, async () => {
  await nextTick(); // the map container renders once `data` is set
  initMap();
  map?.invalidateSize();
  draw();
});
onBeforeUnmount(() => {
  map?.remove();
  map = null;
});
</script>

<template>
  <PageLayout wide>
    <div class="mb-1 flex items-start justify-between gap-4">
      <div>
        <h1 class="text-xl font-semibold">
          Workforce map
        </h1>
        <p class="text-sm text-neutral-500">
          Where each worker last signed on, from the GPS captured at briefing
          sign-on. Pins are coloured by recency.
        </p>
      </div>
    </div>

    <div
      v-if="isPending"
      class="mt-4 text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="mt-4 rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div v-else-if="data">
      <!-- Summary cards -->
      <div class="my-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
          <p class="text-2xl font-semibold text-semantic-success">
            {{ data.on_site_today }}
          </p>
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            On site today
          </p>
        </div>
        <div class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
          <p class="text-2xl font-semibold">
            {{ data.within_7d }}
          </p>
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            Signed on this week
          </p>
        </div>
        <div class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
          <p class="text-2xl font-semibold">
            {{ data.with_pins }}
          </p>
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            With a location pin
          </p>
        </div>
        <div class="rounded-xl border border-brand-primary/30 bg-brand-primary/5 p-4">
          <p class="text-2xl font-semibold text-brand-primary">
            {{ loggedInCount() }}
          </p>
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            Logged in
          </p>
          <p class="mt-0.5 text-xs text-neutral-400">
            {{ usedAppCount() }} used the app<span v-if="lastUsedAt()"> · last {{ ago(lastUsedAt()) }}</span>
          </p>
        </div>
        <div class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
          <p class="text-2xl font-semibold text-neutral-400">
            {{ data.never_seen }}
          </p>
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            Never used the app
          </p>
        </div>
      </div>

      <!-- Map -->
      <div
        ref="mapEl"
        class="wf-map overflow-hidden rounded-xl border border-neutral-200"
      />

      <!-- Legend -->
      <div class="mt-2 flex flex-wrap gap-4 text-xs text-neutral-500">
        <span class="flex items-center gap-1.5">
          <span
            class="inline-block h-3 w-3 rounded-full"
            style="background:#2A9F15"
          /> On site today
        </span>
        <span class="flex items-center gap-1.5">
          <span
            class="inline-block h-3 w-3 rounded-full"
            style="background:#DB562B"
          /> Within 7 days
        </span>
        <span class="flex items-center gap-1.5">
          <span
            class="inline-block h-3 w-3 rounded-full"
            style="background:#A89EA0"
          /> Older
        </span>
        <span class="flex items-center gap-1.5">
          <span class="inline-block h-3 w-3 rounded-full border border-brand-primary" />
          Used the app — no GPS yet (listed below)
        </span>
      </div>

      <!-- No location pin -->
      <section class="mt-6">
        <h2 class="text-sm font-semibold">
          No location pin ({{ noPin().length }})
        </h2>
        <p class="mb-3 text-xs text-neutral-400">
          These workers either haven't signed on via the mobile app yet, or
          signed on without granting location. Their shifts still count; they
          just can't be plotted.
        </p>
        <div
          v-if="noPin().length"
          class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
        >
          <RouterLink
            v-for="w in noPin()"
            :key="w.worker_id"
            :to="`/workers/${w.worker_id}`"
            class="flex items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-0 p-3 hover:bg-neutral-50"
          >
            <WorkerAvatar
              :name="w.name"
              :file-id="w.image_file_id"
              :size="32"
            />
            <div class="min-w-0">
              <p class="truncate text-sm font-medium">
                {{ w.name }}
              </p>
              <p
                class="flex items-center gap-1 text-xs"
                :class="w.last_login ? 'font-medium text-semantic-success' : 'text-neutral-400'"
              >
                <span
                  v-if="w.last_login"
                  class="inline-block h-1.5 w-1.5 rounded-full bg-semantic-success"
                />
                Last logon: {{ ago(w.last_login) }}
              </p>
              <p class="text-xs text-neutral-400">
                Last app use: {{ ago(w.last_seen_at) }}
              </p>
            </div>
          </RouterLink>
        </div>
        <p
          v-else
          class="text-sm text-neutral-400"
        >
          Every active worker has a location pin. 🎉
        </p>
      </section>
    </div>
  </PageLayout>
</template>

<style>
/* Global (Leaflet div-icon markup lives outside scoped styles). */
.wf-map {
  height: 60vh;
  min-height: 380px;
}
.wf-pin {
  display: block;
  width: 16px;
  height: 16px;
  border-radius: 9999px;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25);
}
.wf-pin--pulse {
  animation: wf-pulse 1.6s ease-out infinite;
}
@keyframes wf-pulse {
  0% { box-shadow: 0 0 0 0 rgba(42, 159, 21, 0.55); }
  70% { box-shadow: 0 0 0 12px rgba(42, 159, 21, 0); }
  100% { box-shadow: 0 0 0 0 rgba(42, 159, 21, 0); }
}
</style>
